import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Star as StarIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useRecipe } from '../context/RecipeContext';
import culinaryGlossaryBackground from '../images/culinary-glossary.png';

const PAGE_URL = 'https://createadish.com/culinary-glossary';
const PAGE_TITLE = 'The Culinary Glossary: Master Every Cooking Technique - Create A Dish';
const PAGE_DESCRIPTION = 'Never get stuck on a recipe step again. This dictionary clarifies common culinary jargon—like "chiffonade," "deglaze," and "fold"—that our AI frequently uses, empowering beginners to cook with the confidence of a seasoned chef.';
const PAGE_IMAGE = 'https://createadish.com/images/culinary-glossary-og.png';

const CulinaryGlossary: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetStarted = () => {
    setMode('generate');
    navigate('/composer');
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="culinary glossary, cooking terms, cooking techniques, kitchen dictionary, chef terminology, cooking tips, beginners guide, culinary education" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={PAGE_URL} />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={PAGE_IMAGE} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={PAGE_URL} />
        
        {/* Structured Data - Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "The Culinary Glossary: Master Every Cooking Technique",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": {
              "@type": "Organization",
              "name": "Create A Dish"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Create A Dish",
              "logo": {
                "@type": "ImageObject",
                "url": "https://createadish.com/logo.png"
              }
            },
            "datePublished": "2025-12-24",
            "dateModified": "2025-12-24",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": PAGE_URL
            }
          })}
        </script>
      </Helmet>

      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        {/* Hero Section */}
        <Box
          sx={{
            height: { xs: '40vh', md: '50vh' },
            backgroundImage: `url(${culinaryGlossaryBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(1px)',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              px: 2,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                mb: 2,
              }}
            >
              The Culinary Glossary
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                mb: 1,
              }}
            >
              Master Every Cooking Technique
            </Typography>
          </Box>
        </Box>

        {/* Content Section */}
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              Culinary Glossary
            </Typography>
          </Breadcrumbs>

          <Typography
            variant="h4"
            component="h2"
            sx={{
              textAlign: 'center',
              mb: 4,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            Your Culinary Dictionary
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            Never get stuck on a recipe step again. This dictionary clarifies common culinary jargon—like "chiffonade," "deglaze," and "fold"—that our AI frequently uses, empowering beginners to cook with the confidence of a seasoned chef. We've compiled an extensive alphabetical list of terms drawn from classic and modern culinary practices, with clear definitions to enhance your understanding and cooking skills.
          </Typography>

          {/* A Section */}
          <Box sx={{ mb: 5 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              A
            </Typography>
            <Box component="ul" sx={{ listStyleType: 'none', pl: 0 }}>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>A la carte</strong>: Separately priced items from a menu, not as part of a set meal.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>A la grecque</strong>: Served in the Greek style of cooking, with olive oil, lemon juice, and several seasonings, often referring to vegetables.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>A la mode</strong>: In the fashion; often referring to desserts served with ice cream.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>A Point</strong>: Cooking until the ideal degree of doneness, often referring to meat as medium rare.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Acid</strong>: A substance that neutralizes a base (alkaline) in a liquid solution; foods like citrus juice, vinegar, and wine that have a sour or sharp flavor; acids have a pH of less than 7.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Aerate</strong>: To incorporate air into a mixture by sifting or mixing.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Aging</strong>: Keeping meats or cheeses in a controlled environment to improve tenderness and flavor.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Aioli</strong>: A sauce made from olive oil, garlic, and lemon juice, similar to mayonnaise.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Al Dente</strong>: To the bite; the correct degree of doneness for pasta and vegetables.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Allspice</strong>: An aromatic spice from the dried berry of the West Indian allspice tree, tasting like cinnamon, cloves, nutmeg, and pepper.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Anise</strong>: Aromatic, licorice-flavored herb with feathery leaves and seeds used in cooking and liqueurs.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Antipasto</strong>: Italian for snacks served before a meal; includes cured meats, olives, etc.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Aperitif</strong>: Pre-dinner drink to stimulate the palate.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Appetizers</strong>: Small portioned hot or cold foods; first courses.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Arborio</strong>: Starchy, short-grain rice for risotto.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Aromatic</strong>: Ingredients such as herbs, spices, and vegetables used to add flavor and aroma to dishes.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Arrowroot</strong>: Starch from the arrowroot plant; used as a thickener.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Aspic</strong>: A clear jelly made from clarified stock thickened with gelatin; used to coat foods.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Au Gratin</strong>: Sprinkled with breadcrumbs and cheese, or both, and browned.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Au Jus</strong>: Served with its own juices from cooking.</Typography>
            </Box>
          </Box>

          {/* B Section */}
          <Box sx={{ mb: 5 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              B
            </Typography>
            <Box component="ul" sx={{ listStyleType: 'none', pl: 0 }}>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Bacon</strong>: Cured and smoked pork belly.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Bain Marie</strong>: Hot water bath for gentle cooking or keeping food hot.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Bake</strong>: To cook in an oven at moderate temperatures.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Baking Powder</strong>: Mixture of sodium bicarbonate and acids to leaven baked goods.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Ballotine</strong>: Boned, stuffed, and rolled item, often poultry or fish.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Barbecue</strong>: To cook over open flame or grill.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Barding</strong>: Covering meat with fat to keep moist.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Base</strong>: A substance with pH higher than 7; also known as alkali.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Basic Sauces</strong>: Leading or mother sauces: béchamel, velouté, espagnole, tomato, hollandaise.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Baste</strong>: To moisten foods with juices or fat during cooking.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Batter</strong>: Pourable mixture for pancakes, muffins; or to coat food before frying.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Beat</strong>: To mix vigorously.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Béchamel</strong>: Sauce from roux and milk.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Beurre Blanc</strong>: Emulsified butter sauce with shallots and white wine.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Beurre Manié</strong>: Equal flour and butter for thickening.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Bind/Binder</strong>: To add a thickening ingredient to hold together.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Bisque</strong>: Thick, creamy soup from shellfish or game.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Blacken</strong>: To cook in hot skillet for dark crust.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Blanch</strong>: To quickly boil, often partially cooking food.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Blend</strong>: To stir until combined.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Boil</strong>: To cook in rapidly bubbling liquid.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Bone/Debone</strong>: To remove bones from meat, poultry, or fish.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Braise</strong>: To cook partially immersed in liquid using low heat.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Bread</strong>: To coat with bread crumbs or flour before cooking.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Brine</strong>: Strong mixture of water, salt, and vinegar for flavor and tenderness.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Broil</strong>: To cook with high heat using the top oven element.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Broth</strong>: Flavorful liquid from bones and meat; often interchangeable with stock.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Brown</strong>: To cook until brown but not burned.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Butterfly</strong>: To cut food down the center almost but not completely through.</Typography>
            </Box>
          </Box>

          {/* C Section */}
          <Box sx={{ mb: 5 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              C
            </Typography>
            <Box component="ul" sx={{ listStyleType: 'none', pl: 0 }}>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Can</strong>: To preserve food in a glass jar using safe canning procedures.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Caramelize</strong>: To heat sugar to golden syrup or cook food until soft and golden.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Carve</strong>: To cut food into slices.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Casserole</strong>: Sturdy baking dish or pan.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Chiffonade</strong>: To shred leafy foods finely with a knife.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Chill</strong>: To cool in refrigerator until completely cooled.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Chop</strong>: To cut into small pieces.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Clarify</strong>: To remove solids from liquid for clarity.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Coats a Spoon</strong>: Cooked mixture leaves thin layer on spoon.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Coddle</strong>: To cook eggs in simmering water.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Combine</strong>: To stir until mixed.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Concasse</strong>: To roughly chop peeled and seeded food, often tomatoes.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Confit</strong>: Meat cooked slowly in its own fat.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Consommé</strong>: Clear soup from clarified stock.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Core</strong>: To remove the center of fruit or vegetable.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Coulis</strong>: Thick sauce from fruit or vegetable puree.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Cream</strong>: To beat fat with sugar until light.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Crimp or Flute</strong>: To press pastry edges to seal and decorate.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Crumble</strong>: To break into small pieces.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Crush</strong>: To reduce to crumbs or powder.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Cube</strong>: To cut into equal-sided squares.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Curdle</strong>: To separate a mixture from overcooking.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Cut In</strong>: To mix cold fat with flour until crumbly.</Typography>
            </Box>
          </Box>

          {/* D-Z Sections in compact format */}
          <Box sx={{ mb: 5 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              D - Z
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
              Additional culinary terms from D through Z covering essential cooking techniques and terminology.
            </Typography>
            <Box component="ul" sx={{ listStyleType: 'none', pl: 0 }}>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Deglaze</strong>: To add liquid to pan to release browned bits.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Dice</strong>: To cut into small cubes.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Drain</strong>: To pour off liquid through strainer.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Dredge</strong>: To coat with dry ingredient before cooking.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Emulsify</strong>: To combine ingredients that don't mix into a creamy mixture.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Fillet</strong>: Boneless piece of meat, poultry, or fish.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Flambé</strong>: To add alcohol to hot pan for flames.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Fold</strong>: To gently mix lighter substance into heavier one.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Garnish</strong>: To decorate finished food.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Glaze</strong>: To coat with thin glossy mixture.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Grate</strong>: To shred with a grater.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Grill</strong>: To cook on grate over heat source.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Julienne</strong>: To cut into matchsticks.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Knead</strong>: To work dough into smooth ball.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Macerate</strong>: To soften using liquid.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Marinate</strong>: To soak in seasoned liquid.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Mince</strong>: To chop finely.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Mise en Place</strong>: Preparation of ingredients before cooking.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Poach</strong>: To cook gently in barely simmering water.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Puree</strong>: To blend into smooth paste.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Reduce</strong>: To boil liquid to evaporate moisture and concentrate flavor.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Roast</strong>: To cook at high temperatures in oven for browning.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Roux</strong>: Paste of flour and butter for thickening.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Sauté</strong>: To cook quickly in small fat over high heat.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Sear</strong>: To brown over high heat.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Simmer</strong>: To heat so bubbles gently.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Steam</strong>: To cook with vaporized water.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Steep</strong>: To soak dry ingredients in liquid for flavor.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Stir-Fry</strong>: To cook quickly in small fat while moving food.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Temper</strong>: To slowly add hot liquid to cold ingredient.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Whip</strong>: To beat rapidly to incorporate air.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Whisk</strong>: To quickly mix air into ingredients; also a tool.</Typography>
              <Typography component="li" sx={{ mb: 1.5 }}><strong>Zest</strong>: Outer rind of citrus fruit.</Typography>
            </Box>
          </Box>

          <Typography variant="body1" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold', fontSize: '1.1rem' }}>
            Master these terms and cook with confidence using CreateADish.com's AI-generated recipes!
          </Typography>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Ready to Create Your Perfect Dish?
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<StarIcon />}
              onClick={handleGetStarted}
              sx={{
                minWidth: 200,
                fontSize: '1.1rem',
                py: 1.5,
                px: 4,
              }}
            >
              Get Started
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              © 2025 Create A Dish. Made with ❤️ for food lovers everywhere.
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default CulinaryGlossary;
